import { PrepareData } from '../components/tabs/SingleEventAnalysisTab';
import { AIPurchasedTicket } from './seat';

/**
 * Interface representing raw ticket source data from the API
 */
export interface RawTicketSourceData {
    timestamp: string;
    percentChangeOldest?: number;
    percentChangeMonthAgo?: number;
    percentChangeTwoWeeksAgo?: number;
    currentAvailableCount: number;
    currentAvailableCountExcludeGA?: number;
    totalTicketnetworkConfirmedSalesCount?: number;
    twoWeeksTicketnetworkConfirmedSalesCount?: number;
    oldestAvailableCount?: number;
    monthAgoCount?: number;
    twoWeeksAgoCount?: number;
    oldestTimestamp?: string;
    url?: string;
}

/**
 * Class representing the data from a single ticket source (e.g., Primary, Vivid).
 */
export class TicketSource {
    public readonly timestamp: string;
    public readonly percentChangeOldest?: number;
    public readonly percentChangeMonthAgo?: number;
    public readonly percentChangeTwoWeeksAgo?: number;
    public readonly currentAvailableCount: number;
    public readonly currentAvailableCountExcludeGA?: number;
    public readonly totalTicketnetworkConfirmedSalesCount?: number;
    public readonly twoWeeksTicketnetworkConfirmedSalesCount?: number;
    public readonly oldestAvailableCount?: number;
    public readonly monthAgoCount?: number;
    public readonly twoWeeksAgoCount?: number;
    public readonly oldestTimestamp?: string;
    public readonly url?: string;

    /**
     * Creates a new TicketSource instance
     * @param data The raw data object for a ticket source
     */
    constructor (data: RawTicketSourceData) {
        this.timestamp = data.timestamp;
        this.percentChangeOldest = data.percentChangeOldest;
        this.percentChangeMonthAgo = data.percentChangeMonthAgo;
        this.percentChangeTwoWeeksAgo = data.percentChangeTwoWeeksAgo;
        this.currentAvailableCount = data.currentAvailableCount;
        this.currentAvailableCountExcludeGA = data.currentAvailableCountExcludeGA;
        this.totalTicketnetworkConfirmedSalesCount = data.totalTicketnetworkConfirmedSalesCount;
        this.twoWeeksTicketnetworkConfirmedSalesCount = data.twoWeeksTicketnetworkConfirmedSalesCount;
        this.oldestAvailableCount = data.oldestAvailableCount;
        this.monthAgoCount = data.monthAgoCount;
        this.twoWeeksAgoCount = data.twoWeeksAgoCount;
        this.oldestTimestamp = data.oldestTimestamp;
        this.url = data.url;
    }

    /**
     * A helper getter to return the timestamp as a Date object.
     */
    get timestampAsDate (): Date {
        return new Date(this.timestamp);
    }
}

/**
 * Interface representing raw event data from the API Kibana
 */
export interface RawEventData {
    // id from kibana has format as ""etix-36862468-20251122""
    id: string;
    sm_venue_id?: number | null;
    sm_category_id?: number;
    sm_performer_id?: number;
    sm_event_venue?: string;
    category_name?: string;
    event_type?: string;
    performer_name?: string;
    timestamp?: string;
    type?: string;
    siteId?: string;
    venue?: string;
    dateUtc: string;
    dateLocalStr?: string;
    name: string;
    primary?: RawTicketSourceData;
    ticketNetwork?: RawTicketSourceData;
    vivid?: RawTicketSourceData;
    goTickets?: RawTicketSourceData;
    /** Display text for the Analyze button */
    dAnalyze?: string;
    /** Additional properties */
    [key: string]: any;
}

/**
 * Class representing a single event item.
 */
export class EventItem {
    public readonly id?: number | string | bigint; // id from kibana has format as "etix-36862468-20251122", from history dashboard is number
    public readonly analysisId?: number | bigint | null;
    public readonly eventId?: number | bigint | null;
    public readonly venueId?: number | null;
    public readonly categoryId?: number;
    public readonly performerId?: number;
    public readonly smEventVenue?: string;
    public readonly categoryName?: string;
    public readonly eventType?: string;
    public readonly performerName?: string;
    public readonly state?: string;
    public readonly city?: string;
    public readonly timestamp?: string;
    public readonly type?: string;
    public readonly siteId?: string;
    public readonly venue?: string;
    public readonly venueLayoutType?: string;
    public readonly dateUtc?: string | undefined;
    public readonly dateLocalStr?: string;
    public readonly name?: string;
    public readonly primary?: TicketSource | null;
    public readonly ticketNetwork?: TicketSource | null;
    public readonly vivid?: TicketSource | null;
    public readonly goTickets?: TicketSource | null;
    public readonly isFromHistory?: boolean;
    public readonly deliveryMethod?: number | null;
    public readonly liveTickets?: any[];
    public readonly url?: string;
    public readonly delayDays?: number | null;
    public readonly featuresJson?: string | null;
    public readonly isPurchasedSeat?: boolean;
    public readonly purchasedTicket?: AIPurchasedTicket | null;
    public readonly prepareData?: PrepareData | null;

    private constructor (init: Partial<EventItem>) {
        Object.assign(this, init);
    }

    /**
     * Creates a new EventItem instance
     * @param data The raw data object for a single event
     */
    public static fromRawEventData (data: RawEventData): EventItem {
        // Mapping snake_case to camelCase for clean code
        return new EventItem({
            id : data.id,
            venueId : data.sm_venue_id,
            categoryId : data.sm_category_id,
            performerId : data.sm_performer_id,
            smEventVenue : data.sm_event_venue,
            categoryName : data.category_name,
            eventType : data.event_type,
            performerName : data.performer_name,
            timestamp : data.timestamp,
            type : data.type,
            siteId : data.siteId,
            venue : data.venue,
            dateUtc : data.dateUtc,
            dateLocalStr : data.dateLocalStr,
            name : data.name,

            // TicketSource null if data.TicketSource null
            primary : data.primary ? new TicketSource(data.primary) : null,
            ticketNetwork : data.ticketNetwork ? new TicketSource(data.ticketNetwork) : null,
            vivid : data.vivid ? new TicketSource(data.vivid) : null,
            goTickets : data.goTickets ? new TicketSource(data.goTickets) : null,
        });
    }

    /**
     * Convert EventItem back to RawEventData
     */
    public static toRawEventData (data: EventItem): RawEventData {
        return {
            id: data.id?.toString() || '',
            sm_venue_id: data.venueId,
            sm_category_id: data.categoryId,
            sm_performer_id: data.performerId,
            sm_event_venue: data.smEventVenue,
            category_name: data.categoryName,
            event_type: data.eventType,
            performer_name: data.performerName,
            timestamp: data.timestamp,
            type: data.type,
            siteId: data.siteId,
            venue: data.venue,
            dateUtc: data.dateUtc || '',
            dateLocalStr: data.dateLocalStr || '',
            name: data.name || '',
            primary: data.primary ? data.primary : undefined,
            ticketNetwork: data.ticketNetwork ? data.ticketNetwork : undefined,
            vivid: data.vivid ? data.vivid : undefined,
            goTickets: data.goTickets ? data.goTickets : undefined
        };
    }

    /**
     * Creates a new EventItem instance from history dashboard data
     * @param data The raw data object from EventHistoryDashboard
     */
    public static fromHistoryDashboard (data: EventHistoryDashboard): EventItem  {
        // Mapping snake_case to camelCase for clean code
        return new EventItem({
            id : data.id || data.latestAnalysisId,
            eventId : data.eventId,
            categoryName : data.category_name,
            // eventType : data.eventType,
            // performerName : data.performerName,
            timestamp : data.analysisTimestamp,
            type : data.type,
            siteId : data.siteId,
            venue : data.venue,
            dateUtc : data.dateUtc,
            name : data.name,
            state : data.state,
            city : data.city,
            isFromHistory: true,
            venueId : data.venueId,
            deliveryMethod : data.deliveryMethod,
            delayDays : data.delayDays,
            url : data.url,
            // // TicketSource null if data.TicketSource null
            // primary : data.primary ? new TicketSource(data.primary) : null,
            // ticketNetwork : data.ticketNetwork ? new TicketSource(data.ticketNetwork) : null,
            // vivid : data.vivid ? new TicketSource(data.vivid) : null,
            // goTickets : data.goTickets ? new TicketSource(data.goTickets) : null,
        });
        
    }

    /**
     * Creates a new EventItem instance from prepare data
     * @param data The raw data object from PrepareData
     */
    public static fromPrepareData (data: PrepareData): EventItem  {
        // Mapping snake_case to camelCase for clean code
        return new EventItem({
            id : data.event?.id,
            eventId : data.event?.id,
            venueId : data.venueId,
            categoryName : data.eventCategoryName,
            timestamp : data.event_time ? new Date(data.event_time).toISOString() : undefined,
            type : data.event?.type?.trim(),
            siteId : data.event?.siteId,
            venue : data.event?.venue,
            dateUtc : data.event?.dateTime,
            name : data.event?.name,
            deliveryMethod : data.deliveryMethod ?? undefined,
            liveTickets : data.liveTickets || [],
            url : data.event?.url,
            venueLayoutType: data.venueLayoutType
        });
        
    }

    /**
     * Create a new EventItem instance from AI_EventFeatures
     * @param data The raw data object from AI_EventFeatures
     */
    public static fromAIEventFeatures (data: AIEventFeatures): EventItem  {
        // Mapping snake_case to camelCase for clean code
        return new EventItem({
            id : data.eventId,
            analysisId: data.analysisId,
            eventId : data.eventId,
            venueId : data.venueId || null,
            categoryName : data.eventCategoryName,
            timestamp : data.createdAt,
            type : data.marketType?.trim(),
            siteId : data.siteId,
            venue : data.venueName,
            dateUtc : data.eventDate,
            name : data.eventName,
            deliveryMethod : data.deliveryMethod || null,
            delayDays : data.delayDays || null,
            url : data.primaryUrl,
            featuresJson : data.featuresJson || null
        });
    }

    /**
     * Create a new EventItem instance from AIPurchasedTicket
     * @param data The raw data object from AIPurchasedTicket
     */
    public static fromPurchasedTicket (data: AIPurchasedTicket): EventItem  {
        // Mapping snake_case to camelCase for clean code
        return new EventItem({
            analysisId: data.singleAnalysisId,
            name : data.eventName,
            venue : data.venue,
            dateUtc : data.eventDate,
            siteId : data.siteId,
            type : data.marketType,
            primary : { oldestTimestamp: data.onSaleDate } as TicketSource,
            city : data.city,
            state : data.state,
            categoryName : data.eventCategoryName,
            isPurchasedSeat: true,
            purchasedTicket: data,
            deliveryMethod: data.deliveryMethod || null,
        });
    }
}

/**
 * Interface representing the raw API response data
 */
export interface RawEventApiResponseData {
    items?: RawEventData[];
    total?: number;
}

/**
 * The main class representing the entire API response.
 */
export class EventApiResponse {
    public readonly items: EventItem[];
    public readonly total?: number;

    /**
     * Creates a new EventApiResponse instance
     * @param data The entire raw JSON response from the API
     */
    constructor (data: RawEventApiResponseData) {
        this.items = data.items?.map(item => EventItem.fromRawEventData(item)) || [];
        this.total = data.total;
    }
}

export interface EventHistoryDashboard {
    id: number | undefined;
    // for mapping fields in analysis page
    name: string;
    venue: string | undefined;
    score: number | undefined;
    url: string | undefined;
    currentAvailableCount: number | undefined;
    oldestAvailableCount: number | undefined;
    dateUtc: string | undefined;
    oldestTimestamp: string | undefined;
    category_name: string | undefined; // Giữ nguyên vì có dấu gạch dưới
    type: string | undefined;

    // Mapping only props in view V_EventHistoryDashboard
    deliveryMethod: number | undefined;
    delayDays: number | undefined;
    eventId: number;
    venueId: number | undefined;
    siteId: string | undefined;
    eventName: string | undefined;
    eventDate: string | undefined;
    onSaleDate: string | undefined;
    primaryUrl: string | undefined;
    marketType: string | undefined;
    eventCategoryName: string | undefined;
    locationCountry: string | undefined;
    performerPopularityScore: number | undefined;
    venueName: string | undefined;
    state: string | undefined;
    city: string | undefined;
    venueCapacity: number | undefined;
    latestAnalysisId: number;
    bulkAnalysisId: number;
    analysisTimestamp: string;
    overallScore: number | undefined;
    totalPositiveScore: number | undefined;
    totalRiskScore: number | undefined;
    confidenceLevel: number | undefined;
    recommendationDecision: string | undefined;
    recommendationConfidence: string | undefined;
    modelVersion: string | undefined;
    analysisStatus: string | undefined;
    manualOverallScore: number | undefined;
    manualAnalysisTimestamp: string | undefined;
    bulkOverallScore: number | undefined;
    bulkAnalysisTimestamp: string | undefined;
    isSingleShowAtVenue: boolean | undefined;
    venueLayoutType: string | undefined;
    isPostponed: boolean | undefined;
    primarySalesVelocity30d: number | undefined;
    primaryTicketSalesPerformance: string | undefined;
    primaryPriceLevelChange: number | undefined;
    primaryPercentLeft: number | undefined;
    primaryCurrentAvailabilityCount: number | undefined;
    tnTotalSales: number | undefined;
    secondariesCompetitionCount: number | undefined;
    secondariesPriceTrend: string | undefined;
    artistHasSpecSalesHistory: boolean | undefined;
    artistHasRealSalesHistory: boolean | undefined;
    isArtistInExpiredInventory: boolean | undefined;
    isVenueDifficultToBuy: boolean | undefined;
    easeOfPurchase: string | undefined;
    hasSeatMap: string | undefined;
    isSeasonalEvent: boolean | undefined;
    featuresTimestamp: string | undefined;
    scoreUpdatedAt: string;
    mL_Score: number | undefined;
    mL_StrongBuyCount: number | undefined;
    mL_BuyCount: number | undefined;
    simplifiedScore: number | undefined;
    liquidityConsistencyScore: number | undefined;
}
export interface AISingleEventAnalysis {
    id: number;
    eventId: bigint;
    eventName?: string;
    siteId?: string;
    analysisTimestamp: string; // Typically a string in ISO 8601 format
    analysisStatus: string;
    exclusionReason: string | null;
    modelVersion: string | null;
    overallScore: number | null;
    totalPositiveScore: number | null;
    totalRiskScore: number | null;
    confidenceLevel: number | null;
    recommendationDecision: string | null;
    recommendationConfidence: string | null;
    positiveFactors: string | null; // Often a JSON string, can be typed further
    riskFactors: string | null;     // Often a JSON string, can be typed further
    scoreBreakdown: string | null;  // Often a JSON string, can be typed further
    userId: string | null;
    analysisSource: string | null;
    isActive: boolean;
    featuresJson: string | null;
    venueId: number | null;
    performerId: number | null;
    performerName: string | null;
    haveSeatAnalysis?: boolean;
}

export interface AIEventFeatures {
    id: number;
    analysisId: number;
    eventId: number;
    siteId?: string;
    eventName?: string;
    eventDate?: string;
    onSaleDate?: string;
    primaryUrl?: string;
    marketType?: string;
    eventCategoryName?: string;
    locationCountry?: string;
    performerPopularityScore?: number;
    venueName?: string;
    state?: string;
    city?: string;
    venueCapacity?: number;
    isSingleShowAtVenue?: boolean;
    venueLayoutType?: string;
    isPostponed?: boolean;
    primarySalesVelocity30d?: number;
    primaryTicketSalesPerformance?: string;
    primaryPriceLevelChange?: number;
    primaryPercentLeft?: number;
    primaryCurrentAvailabilityCount?: number;
    tnTotalSales?: number;
    secondariesCompetitionCount?: number;
    secondariesPriceTrend?: string;
    artistHasSpecSalesHistory?: boolean;
    artistHasRealSalesHistory?: boolean;
    isVenueDifficultToBuy?: boolean;
    easeOfPurchase?: string;
    isArtistInExpiredInventory?: boolean;
    hasSeatMap?: string;
    isSeasonalEvent?: boolean;
    createdAt: string;
    createdBy?: string;
    featuresJson?: string;
    venueId?: number;
    performerId?: number;
    deliveryMethod?: number;
    delayDays?: number;
}